import {FC, useEffect, useState, useRef} from 'react';
import validator from 'validator';
import {Box, IconButton, Paper, Popper, Typography} from '@mui/material';
import ArrButton from '../../arr-button/ArrButton';
import {ReactComponent as CloseIcon} from '../../../../assets/images/close-base-icon.svg';
import './FilterSelectionModal.scss';
import ArrRadio from '../../arr-radio/ArrRadio';
import ArrSingleSelectDropdown from '../../arr-select-dropdown/ArrSingleSelectDropdown';
import ArrDatePicker from '../../arr-date-picker/ArrDatePicker';
import {
  ActiveFilter,
  filterSelectionType,
} from '../filter-accordion/ArrFilterAccordion';
import {t} from 'i18next';
import ArrTextField from '../../arr-text-field/ArrTextField';
import LazyLoadFilterList from './LazyLoadFilterList';

export const FILTER_SELECTION_MAP: {[key: string]: string} = {
  between: 'Between',
  before: 'Before',
  after: 'After',
  on: 'On',
  include: 'Include',
  exclude: 'Exclude',
  below: 'Below',
  over: 'Over',
  at: 'At',
};

type FilterSelectionModalProps = {
  filterSelectionData: ActiveFilter;
  anchorElement: HTMLElement | null;
  onAddToFilters: (filterData: ActiveFilter) => void;
  onClose: () => void;
  isUpdateModel: boolean;
  closeOnEsc?: boolean;
};
const FilterSelectionModal: FC<FilterSelectionModalProps> = ({
  filterSelectionData,
  anchorElement,
  onAddToFilters,
  onClose,
  isUpdateModel,
  closeOnEsc,
}) => {
  const [filterData, setFilterData] = useState<ActiveFilter>(
    {} as ActiveFilter
  );
  const [isAddToFiltersDisabled, setIsAddToFiltersDisabled] = useState(true);
  const [isFromSizeError, setIsFromSizeError] = useState<boolean>(false);
  const [isToSizeError, setIsToSizeError] = useState<boolean>(false);
  const [fromError, setFromError] = useState<string>('');
  const [toError, setToError] = useState<string>('');
  const [enteredFromSize, setEnteredFromSize] = useState<string>('');
  const [enteredToSize, setEnteredToSize] = useState<string>('');
  const [enteredStartDate, setEnteredStartDate] = useState<string>('');
  const [enteredEndDate, setEnteredEndDate] = useState<string>('');
  const [dateErrors, setDateErrors] = useState({
    startDate: '',
    endDate: '',
  });
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEsc) {
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc, true);

    return () => {
      window.removeEventListener('keydown', handleEsc, true);
    };
  }, [onClose, closeOnEsc]); 
  useEffect(() => {
    const normalizeFilter = (f: any) => {
      const values = Array.isArray(f?.values) ? f.values : [];
      const stableValues = values.map((v: any) =>
        typeof v === 'object' ? JSON.stringify(v) : String(v)
      );
      stableValues.sort();

      return {
        type: f?.type ?? '',
        selectionType: f?.selectionType ?? '',
        values: stableValues,
        startDate: f?.startDate ?? '',
        endDate: f?.endDate ?? '',
        startSize: f?.startSize ?? '',
        endSize: f?.endSize ?? '',
        label: f?.label ?? '',
      };
    };
    const normalizeFilterData = normalizeFilter(filterData);
    const normalizeFilterSelectionData = normalizeFilter(filterSelectionData);
    let buttonDisabled = false;
    if (
      normalizeFilterData.type === 'NVARCHAR' &&
      !normalizeFilterData.values?.length
    ) {
      buttonDisabled = true;
    } else if (
      normalizeFilterData.type === 'DATETIME' &&
      normalizeFilterData.selectionType !== 'between' &&
      !normalizeFilterData.startDate
    ) {
      buttonDisabled = true;
    } else if (
      normalizeFilterData.type === 'DATETIME' &&
      normalizeFilterData.selectionType === 'between' &&
      (!normalizeFilterData.startDate || !normalizeFilterData.endDate)
    ) {
      buttonDisabled = true;
    } else if (
      normalizeFilterData.type === 'INT' &&
      normalizeFilterData.selectionType !== 'between' &&
      !normalizeFilterData.startSize
    ) {
      buttonDisabled = true;
    } else if (
      normalizeFilterData.type === 'INT' &&
      normalizeFilterData.selectionType === 'between' &&
      (!normalizeFilterData.startSize || !normalizeFilterData.endSize)
    ) {
      buttonDisabled = true;
    } else if (
      !normalizeFilterData.endDate &&
      !normalizeFilterData.endSize &&
      !normalizeFilterData.selectionType &&
      !normalizeFilterData.startDate &&
      !normalizeFilterData.startSize &&
      !normalizeFilterData.type &&
      !normalizeFilterData.values.length
    ) {
      buttonDisabled = true;
    }
    if (isUpdateModel) {
      if (
        normalizeFilterSelectionData.startDate &&
        normalizeFilterSelectionData.endDate
      ) {
        const hasValue = (v?: string) => Boolean(v?.trim());
        buttonDisabled = !(
          (hasValue(normalizeFilterData.startDate) &&
            normalizeFilterData.startDate !==
              normalizeFilterSelectionData.startDate) ||
          (hasValue(normalizeFilterData.endDate) &&
            normalizeFilterData.endDate !==
              normalizeFilterSelectionData.endDate)
        );
      } else {
        const hasChanged =
          JSON.stringify(normalizeFilterData) !==
          JSON.stringify(normalizeFilterSelectionData);
        if (!hasChanged) {
          buttonDisabled = true;
        }
      }
    }
    setIsAddToFiltersDisabled(buttonDisabled);
  }, [filterData, isUpdateModel, filterSelectionData]);
  useEffect(() => {
    const incomingFilterData = {...filterSelectionData};
    if (
      filterSelectionData.type === 'NVARCHAR' &&
      !filterSelectionData.selectionType
    ) {
      incomingFilterData.selectionType = 'include';
    }
    if (
      (filterSelectionData.type === 'DATETIME' ||
        filterSelectionData.type === 'INT') &&
      !filterSelectionData.selectionType
    ) {
      incomingFilterData.selectionType = 'between';
    }
    setFilterData(incomingFilterData);
  }, [filterSelectionData]);
  useEffect(() => {
    if (anchorElement) {
      // Reset all error states
      setDateErrors({startDate: '', endDate: ''});
      setEnteredFromSize('');
      setEnteredToSize('');
      setFromError('');
      setToError('');
      setIsFromSizeError(false);
      setIsToSizeError(false);
    }
  }, [anchorElement]);
  useEffect(() => {
    setDateErrors({startDate: '', endDate: ''});
    setEnteredToSize('');
    setEnteredFromSize('');
    setFromError('');
    setToError('');
    setEnteredStartDate('');
    setEnteredEndDate('');
    setIsToSizeError(false);
    setIsFromSizeError(false);

    setFilterData((prev) => ({...prev}));
  }, [filterData.selectionType]);
  


  const isValidYYYYMMDD = (value: string): boolean => {
    if (!value) return false;
    const regex = /^\d{4}\/\d{2}\/\d{2}$/;
    if (!regex.test(value)) return false;
    const [y, m, d] = value.split('/');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return (
      date.getFullYear() === Number(y) &&
      date.getMonth() === Number(m) - 1 &&
      date.getDate() === Number(d)
    );
  };
  const isEndDateAfterStart = (start: string, end: string) => {
    if (!isValidYYYYMMDD(start) || !isValidYYYYMMDD(end)) return false;
    return new Date(end) > new Date(start);
  };

  const handleStartDateChange = (value: string) => {
    const date = value;
    const endDateToCompare = enteredEndDate
      ? enteredEndDate
      : filterData.endDate;
    setEnteredStartDate(value);
    if (!date) {
      setDateErrors((prev) => ({...prev, startDate: ''}));
      return;
    }
    if (!isValidYYYYMMDD(date)) {
      setFilterData((prev) => ({
        ...prev,
        startDate: '',
      }));
      setDateErrors((prev) => ({
        ...prev,
        startDate: t('arrFilterAccordion.invalidDateError'),
      }));
      return;
    }
    if (endDateToCompare && !isEndDateAfterStart(date, endDateToCompare)) {
      setFilterData((prev) => ({...prev, startDate: ''}));

      setDateErrors((prev) => ({
        ...prev,
        startDate: t('arrFilterAccordion.startDateBeforeEndDate'),
      }));
      return;
    }
    setDateErrors({startDate: '', endDate: ''});
    setFilterData((prev) => ({
      ...prev,
      startDate: date,
      endDate: endDateToCompare,
    }));
  };

  const handleEndDateChange = (value: string) => {
    setEnteredEndDate(value);
    const startDateToCompare = enteredStartDate
      ? enteredStartDate
      : filterData.startDate;
    const date = value;
    if (!date) {
      setDateErrors((prev) => ({...prev, endDate: ''}));
      return;
    }
    if (!isValidYYYYMMDD(date)) {
      setFilterData((prev) => ({...prev, endDate: ''}));

      setDateErrors((prev) => ({
        ...prev,
        endDate: t('arrFilterAccordion.invalidDateError'),
      }));
      return;
    }

    if (startDateToCompare && !isEndDateAfterStart(startDateToCompare, date)) {
      setFilterData((prev) => ({...prev, endDate: ''}));

      setDateErrors((prev) => ({
        ...prev,
        endDate: t('arrFilterAccordion.endDateAfterStartDate'),
      }));
      return;
    }
    setDateErrors({endDate: '', startDate: ''});
    setFilterData((prev) => ({
      ...prev,
      endDate: date,
      startDate: startDateToCompare,
    }));
  };

  const handleSizeInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {value, name} = event.target;
    const isPositiveNumber = (value: string): boolean => {
      // Trim spaces first to allow " 123 "
      return validator.isInt(value.trim(), {min: 0});
    };

    if (value.trim() === '') {
      if (name === 'fromSize') {
        setEnteredFromSize('');
        setFromError('');
        setIsFromSizeError(false);
        setFilterData((prev) => ({...prev, startSize: ''}));
      }
      if (name === 'toSize') {
        setEnteredToSize('');
        setToError('');
        setIsToSizeError(false);
        setFilterData((prev) => ({...prev, endSize: ''}));
      }
      return;
    }

    if (name === 'fromSize') {
      const toSizeToCompare = enteredToSize
        ? enteredToSize
        : filterData.endSize;

      setEnteredFromSize(value);
      if (isPositiveNumber(value)) {
        if (toSizeToCompare) {
          if (Number(value) < Number(toSizeToCompare)) {
            setFromError('');
            setIsFromSizeError(false);
            setToError('');
            setIsToSizeError(false);
            setFilterData((prev) => ({
              ...prev,
              startSize: value,
              endSize: toSizeToCompare,
            }));
          } else {
            setIsFromSizeError(true);
            setFromError(t('arrFilterAccordion.fromSizeLessThanToSize'));
            setFilterData((prev) => ({...prev, startSize: ''}));
          }
          return;
        }
        setFromError('');
        setIsFromSizeError(false);
        setFilterData((prev) => ({...prev, startSize: value}));
      } else {
        setIsFromSizeError(true);
        setFromError(t('arrFilterAccordion.invalidSizeError'));
        setFilterData((prev) => ({...prev, startSize: ''}));
      }
    }
    if (name === 'toSize') {
      setEnteredToSize(value);
      const fromSizeToCompare = enteredFromSize
        ? enteredFromSize
        : filterData.startSize;

      if (isPositiveNumber(value)) {
        if (fromSizeToCompare) {
          if (Number(value) > Number(fromSizeToCompare)) {
            setFromError('');
            setIsFromSizeError(false);
            setToError('');
            setIsToSizeError(false);
            setFilterData((prev) => ({
              ...prev,
              startSize: fromSizeToCompare,
              endSize: value,
            }));
          } else {
            setIsToSizeError(true);
            setToError(t('arrFilterAccordion.toSizeGreaterThanFromSize'));
            setFilterData((prev) => ({...prev, endSize: ''}));
          }
          return;
        }
        setToError('');
        setIsToSizeError(false);
        setFilterData((prev) => ({...prev, endSize: value}));
      } else {
        setIsToSizeError(true);
        setToError(t('arrFilterAccordion.invalidSizeError'));
        setFilterData((prev) => ({...prev, endSize: ''}));
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const root = modalRef?.current;
    const closeBtn = closeButtonRef.current;
    if (!root || !closeBtn) return;

    // focusable selectors excluding disabled elements
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>(focusableSelector)
    ).filter((el) => el.offsetParent !== null);
    if (nodes.length === 0) {
      event.preventDefault();
      closeBtn.focus();
      return;
    }

    const active = document.activeElement as HTMLElement | null;
    const idx = active ? nodes.indexOf(active) : -1;
    const lastEl = nodes[nodes.length - 1];

    if (event.shiftKey) {
      if (closeBtn.contains(active)) {
        event.preventDefault();
        lastEl.focus();
        return;
      }
      
      if (idx === nodes.length - 1) {
        return;
      }
    }

    if (idx === nodes.length - 1) {
      event.preventDefault();
      closeBtn.focus();
    }
  };

  const radioButtonContainer = (
  <Box className="radio-button-container">  
      <Box>
        <ArrRadio
          dataTestId="include-radio-button"
          aria-label={t('arrFilterAccordion.include')}
          className="radio-button"
          variant="secondary"
          size="xs"
          name="selectionType"
          data={[
            {
              label: t('arrFilterAccordion.include'),
              value: "include",
            },
          ]}
          value={filterData.selectionType ?? ''}
          onChange={() => {
            setFilterData((prev) => {
              return {...prev, selectionType: 'include'};
            });
          }}
          uncheckedTabIndex={0}
          labelTabIndex={-1}
        />
      </Box>
      <Box>
        <ArrRadio
          dataTestId="exclude-radio-button"
          aria-label={t('arrFilterAccordion.exclude')}
          className="radio-button"
          variant="secondary"
          size="xs"
          name="selectionType"
          data={[
            {
              label: t('arrFilterAccordion.exclude'),
              value: "exclude",
            },
          ]}
          value={filterData.selectionType ?? ''}
          onChange={() => {
            setFilterData((prev) => {
              return {...prev, selectionType: 'exclude'};
            });
          }}
          uncheckedTabIndex={0}
          labelTabIndex={-1}
        />
      </Box>
    </Box>
  );
  const multiSelectFilterContainer = (
    <Box>
      <LazyLoadFilterList
        setFilterData={setFilterData}
        filterData={filterData}
        id={filterSelectionData?.id}
      />
    </Box>
  );
  const singleSelectDateSelectionTypeContainer = (
    <Box>
      <ArrSingleSelectDropdown
        dataTestId="asd-single-select-filter-dropdown"
        aria-label={t('arrFilterAccordion.dateRange')}
        dropdownLabel={t('arrFilterAccordion.dateRange')}
        dropdownItemKey="value"
        dropdownItemDisplay="label"
        dropdownItems={[
          {value: 'between', label: t('arrFilterAccordion.between')},
          {value: 'before', label: t('arrFilterAccordion.before')},
          {value: 'after', label: t('arrFilterAccordion.after')},
          {value: 'on', label: t('arrFilterAccordion.on')},
        ]}
        selectedValues={{
          value: filterData.selectionType,
          label: FILTER_SELECTION_MAP[filterData.selectionType ?? 'between'],
        }}
        onSelectionChange={(value) => {
          setFilterData((prev) => {
            return {
              ...prev,
              selectionType: value?.value as filterSelectionType,
              startDate: '',
              endDate: '',
            };
          });
        }}
        tabIndex={-1}
      />
    </Box>
  );
  const singleDateContainer = (
    <Box tabIndex={0}>
      <ArrDatePicker
        key={filterData.selectionType}
        dataTestId={`date-picker-filter-${filterData.selectionType}`}
        aria-label={t('arrFilterAccordion.selectDate')}
        type="date-picker"
        label={t('arrFilterAccordion.selectDate')}
        initialStartDate={filterData.startDate}
        placeholder={t('arrFilterAccordion.datePlaceholder')}
        onChange={(value) => handleStartDateChange(value as string)}
        showToday={true}
        isError={Boolean(dateErrors.startDate)}
        helperText={dateErrors.startDate}
      />
    </Box>
  );
  const multiDateContainer = (
    <Box className="multi-date-container">
      <Box tabIndex={0}>
        <ArrDatePicker
          dataTestId={`multi-start-date-picker-filter-${filterData.selectionType}`}
          aria-label={t('arrFilterAccordion.startDate')}
          type="date-picker"
          label={t('arrFilterAccordion.startDate')}
          initialStartDate={filterData.startDate}
          placeholder="YYYY/MM/DD"
          onChange={(value) => handleStartDateChange(value as string)}
          showToday={true}
          isError={Boolean(dateErrors.startDate)}
          helperText={dateErrors.startDate}
        />
      </Box>
      <Box tabIndex={0}>
        <ArrDatePicker
          dataTestId={`multi-end-date-picker-filter-${filterData.selectionType}`}
          aria-label={t('arrFilterAccordion.endDate')}
          type="date-picker"
          label={t('arrFilterAccordion.endDate')}
          initialStartDate={filterData.endDate}
          placeholder="YYYY/MM/DD"
          onChange={(value) => handleEndDateChange(value as string)}
          showToday={true}
          isError={Boolean(dateErrors.endDate)}
          helperText={dateErrors.endDate}
        />
      </Box>
    </Box>
  );
  const singleSelectFileSizeSelectionTypeContainer = (
    <Box tabIndex={0}>
      <ArrSingleSelectDropdown
        dataTestId="asd-filter-selection-modal-single-select-filter-dropdown"
        aria-label={t('arrFilterAccordion.sizeRange')}
        dropdownLabel={t('arrFilterAccordion.sizeRange')}
        dropdownItemKey="value"
        dropdownItemDisplay="label"
        dropdownItems={[
          {value: 'below', label: t('arrFilterAccordion.below')},
          {value: 'over', label: t('arrFilterAccordion.over')},
          {value: 'at', label: t('arrFilterAccordion.at')},
          {value: 'between', label: t('arrFilterAccordion.between')},
        ]}
        selectedValues={{
          value: filterData.selectionType,
          label: FILTER_SELECTION_MAP[filterData.selectionType ?? 'between'],
        }}
        onSelectionChange={(value) => {
          setFilterData((prev) => {
            return {
              ...prev,
              selectionType: value?.value as filterSelectionType,
              startSize: '',
              endSize: '',
            };
          });
        }}
        tabIndex={-1}
      />
    </Box>
  );

  const fileSizeContainer = (
    <Box className="file-size-container">
      <Box tabIndex={0}>
        <ArrTextField
          key={`from-${filterData.selectionType}`}
          id="from-size-text-field"
          dataTestId="filter-from-size"
          label={
            filterData.selectionType === 'between'
              ? t('arrFilterAccordion.fromSize')
              : t('arrFilterAccordion.size')
          }
          onChange={handleSizeInputChange}
          name="fromSize"
          value={enteredFromSize ? enteredFromSize : filterData.startSize}
          state={isFromSizeError ? 'error' : 'enabled'}
          showErrorIcon={false}
          supportText={isFromSizeError ? fromError : ''}
        />
      </Box>
      {filterData.selectionType === 'between' && (
        <Box tabIndex={0}>
          <ArrTextField
            key={`to-${filterData.selectionType}`}
            id="to-size-text-field"
            dataTestId="filter-to-size"
            state={isToSizeError ? 'error' : 'enabled'}
            label={t('arrFilterAccordion.toSize')}
            onChange={handleSizeInputChange}
            value={enteredToSize ? enteredToSize : filterData.endSize}
            name="toSize"
            showErrorIcon={false}
            supportText={isToSizeError ? toError : ''}
            data-testid="to-size-text-field"
          />
        </Box>
      )}
    </Box>
  );
  return (
    <Popper
      data-testid="arr-filter-selection-modal"
      open={Boolean(anchorElement)}
      anchorEl={anchorElement}
      placement="bottom-start"
      className="arr-filter-selection-modal"
    >
      <Paper
        className="filter-selection-modal"
        ref={modalRef}
        onKeyDown={handleKeyDown}
      >
        <Box className="modal-header">
          <Typography
            className="typo-text subhead4_size title"
            data-testid={`filter-selection-modal-filter-by`}
            tabIndex={0}
          >
            {t('arrFilterAccordion.filterBy', {
              label: filterData.label,
            })}
          </Typography>
          <Box>
            <IconButton
              data-testid="filter-selection-close-button"
              aria-label={t('common.close')}
              size="small"
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && e.shiftKey) return;
              }}
              className="modal-close"
              ref={closeButtonRef}
            >
              <CloseIcon className="close-icon" />
            </IconButton>
          </Box>
        </Box>
        <Box className="primary-selection">
          {filterData.type === 'NVARCHAR' && radioButtonContainer}
          {filterData.type === 'DATETIME' &&
            singleSelectDateSelectionTypeContainer}
          {filterData.type === 'INT' &&
            singleSelectFileSizeSelectionTypeContainer}
        </Box>
        <Box className="secondery-selection">
          {(filterData.selectionType === 'include' ||
            filterData.selectionType === 'exclude') &&
            multiSelectFilterContainer}

          {(filterData.selectionType === 'before' ||
            filterData.selectionType === 'after' ||
            filterData.selectionType === 'on') &&
            singleDateContainer}

          {filterData.selectionType === 'between' &&
            filterData.type === 'DATETIME' &&
            multiDateContainer}

          {filterData.type === 'INT' && fileSizeContainer}
        </Box>
        <Box className="modal-submit">
          <ArrButton
            dataTestId="filter-selection-modal-submit"
            aria-label={
              isUpdateModel
                ? t('arrFilterAccordion.updateFilter')
                : t('arrFilterAccordion.addToFilters')
            }
            size="small"
            disabled={isAddToFiltersDisabled}
            onClick={() => {
              onAddToFilters(filterData);
            }}
            isAriaDisabled={isAddToFiltersDisabled}
            tabIndex={0}
          >
            {isUpdateModel
              ? t('arrFilterAccordion.updateFilter')
              : t('arrFilterAccordion.addToFilters')}
          </ArrButton>
        </Box>
      </Paper>
    </Popper>
  );
};

export default FilterSelectionModal;
